import { ReactElement } from 'react';
import { useParams } from 'react-router-dom';
import { useSessionCalcData, useSessionSummary, useInvalidateSession, useSessions } from '../../hooks/useSessions';
import SessionDetails from './SessionDetails/SessionDetails';
import SessionSimple from './SessionSimple/SessionSimple';
import { SessionAnalysisType, SessionAnalysisTypeUtils } from '../../types/SessionAnalysisType';
import { useFeedbackPageContext } from '../../hooks/useFeedbackPageContext';

export default function SessionView(): ReactElement {
  const { sessionId } = useParams();
  // Cached list (also used by the session list + nav) tells us the analysis
  // type before any heavy per-session fetch fires.
  const { data: allSessions, isPending: isListPending } = useSessions();

  // Must stay above the early returns below — hooks cannot be conditional.
  useFeedbackPageContext({
    page: 'SessionView',
    sessionId: sessionId ?? null,
    analysisType: allSessions?.find((s: any) => s.id === sessionId)?.analysis_type ?? null,
  });

  if (!sessionId) {
    return <div>Missing session id!</div>;
  }

  if (sessionId === 'new') {
    return <NewSessionDetails />;
  }

  // Hold on a cold deep-link until the (cheap, cached) list resolves so the
  // heavy summary/calc queries never fire for singleton sessions.
  if (isListPending) {
    return <div />;
  }

  const analysisType = allSessions?.find((s: any) => s.id === sessionId)?.analysis_type;
  const isSimple = analysisType !== undefined
    && SessionAnalysisTypeUtils.isSingletonLevel(analysisType)
    && analysisType !== SessionAnalysisType.REFERENCE_CALIBRATIONS;
  if (isSimple) {
    return <SessionSimple sessionId={sessionId} />;
  }

  return <ExistingSessionDetails sessionId={sessionId} />;
}

const NewSessionDetails = () => {
  const handleRefetch = async () => {
    // No-op for new sessions
  };

  return (
    <SessionDetails
      sessionId={'new'}
      session={{}}
      isSessionPending={false}
      isSessionFetching={false}
      isNew={true}
      refetchSession={handleRefetch}
    />
  );
};

const ExistingSessionDetails = ({ sessionId }: { sessionId: string }) => {
  // Phase 1: light summary drives the config/run list — paints first.
  const { data: summary, isPending: isSummaryPending, isFetching: isSummaryFetching } = useSessionSummary(sessionId);
  // Phase 2: full payload for plots — fetched in parallel but no longer gates the list.
  const { data: full, isPending: isFullPending, isFetching: isFullFetching } = useSessionCalcData(sessionId);
  const invalidateSession = useInvalidateSession(sessionId);

  return (
    <SessionDetails
      sessionId={sessionId}
      session={summary}
      isSessionPending={isSummaryPending}
      isSessionFetching={isSummaryFetching}
      fullSession={full}
      isFullPending={isFullPending}
      isFullFetching={isFullFetching}
      isNew={false}
      refetchSession={invalidateSession}
    />
  );
};
